import { useRef, useEffect, useState } from 'react';
import Container from '@ui/container/container';
import HoverCard from '@ui/cards/hover-card/hover-card';
import PreHoverContent from '@ui/cards/hover-card/pre-hover-content';
import HoverContent from '@ui/cards/hover-card/hover-content';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject, selectProjectThemePack, selectProjectThemeMode } from '@/store/slices/project/selectors';
import type { LocalCampaignTemplate } from '@/micros/edit-project/grid-areas/main/campaigns/templates';

const TEMPLATE_WIDTH = 520;

interface TemplatePreviewCardProps {
  template: LocalCampaignTemplate;
  onEdit: (templateUuid: string) => void;
  onPreview: (templateUuid: string) => void;
  isCreating?: boolean;
}

export default function TemplatePreviewCard({ template, onEdit, onPreview, isCreating }: TemplatePreviewCardProps) {
  const event = useAppSelector(getWorkingProject);
  const theme = useAppSelector(selectProjectThemePack);
  const themeMode = useAppSelector(selectProjectThemeMode);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      setScale(entry.contentRect.width / TEMPLATE_WIDTH);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const TemplateComponent = template.component;

  return (
    <Container className="flex-column gap-12" data-component="TemplatePreviewCard">
      <HoverCard
        type="fade"
        width="100%"
        aspectRatio="16 / 9"
        containerClassName="radius-12"
        forceHoverState={isCreating}
      >
        <PreHoverContent>
          <div
            ref={containerRef}
            style={{ position: 'relative', overflow: 'hidden', height: '100%', width: '100%', borderRadius: '12px' }}
          >
            {event && theme && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: TEMPLATE_WIDTH,
                  transformOrigin: 'top left',
                  transform: `scale(${scale})`,
                  pointerEvents: 'none',
                }}
              >
                <TemplateComponent
                  event={event}
                  theme={theme}
                  mode={themeMode ?? undefined}
                  // uncomment to render the emails in the list with variables for easier exporting to other repos
                  // calendarLinks={{
                  //   iCal: '${icalLink}',
                  //   outlook: '${outlookLink}',
                  //   google: '${googleLink}',
                  // }}
                  // customizations={{
                  //   subject: '',
                  //   title: '${title}',
                  //   description: '${description}',
                  //   cta: '${cta}',
                  //   startIntro: '${startIntro}',
                  //   salutation: '${salutation}',
                  //   headingFont: '${headingFont}',
                  //   bodyFont: '${bodyFont}',
                  //   headingImage: '${headingImage}',
                  //   mainImage: '${mainImage}',
                  //   headingTextColor: 'var(--heading-text-color)',
                  //   bodyTextColor: 'var(--heading-text-color)',
                  //   backgroundColor: 'var(--background-color)',
                  //   accentColor: 'var(--accent-color)',
                  //   secondaryAccentColor: 'var(--secondary-accent-color)',
                  //   secondaryBackgroundColor: 'var(--secondary-background-color)',
                  //   secondaryImage: '${secondaryImage}',
                  //   containerColor: 'var(--container-color)',
                  //   buttonTextColor: 'var(--button-text-color)',
                  // }}
                />
              </div>
            )}
          </div>
        </PreHoverContent>

        <HoverContent backgroundColor="rgba(var(--gray-1100-rgb), 0.5)">
          <Container className="flex jc-center ai-center gap-12" height="100%" width="100%">
            <Button
              className="L1"
              backgroundColor="var(--gray-700)"
              hoverBackgroundColor="var(--gray-600)"
              onClick={() => onEdit(template.uuid)}
              borderRadius="100px"
              padding="12px 32px"
              disabled={isCreating}
              loading={isCreating}
            >
              Edit
            </Button>
            <Button
              className="L1"
              backgroundColor="var(--primitives-mode-1-pink-400)"
              hoverBackgroundColor="var(--primitives-mode-1-pink-500)"
              color="var(--gray-true-dark-1200)"
              onClick={() => onPreview(template.uuid)}
              borderRadius="100px"
              padding="12px 32px"
              disabled={isCreating}
            >
              Preview
            </Button>
          </Container>
        </HoverContent>
      </HoverCard>

      <Container maxWidth="100%">
        <Text className="H5" clamp={1} design="GlobalIcoText2">
          {template.title}
        </Text>
      </Container>
    </Container>
  );
}
